/**
 * AddAgentWizard.jsx
 * Conversational agent creation wizard.
 * Split-screen: chat left builds the agent live, card preview assembles right.
 *
 * Props:
 *   onClose()          — close the modal
 *   onCreated(agent)   — called with the created/updated agent object
 *   existingAgents[]   — for similarity awareness (passed to API)
 *   editAgent          — if set, wizard starts in edit mode
 *
 * API surface (unchanged):
 *   customAgentAPI.create(payload)         → { agent }
 *   customAgentAPI.update(id, payload)     → { agent }
 *   customAgentAPI.checkSimilarity(...)    → { similar }
 *   Anthropic /v1/messages for conversational AI responses
 *
 * Theme tokens (WorKult light):
 *   bg var(--lyra-color-surface-2) · sidebar white · accent var(--lyra-color-primary) · headings var(--lyra-color-text)
 *   muted var(--lyra-color-text-muted) · card border var(--lyra-color-border) · font DM Sans
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { customAgentAPI, agentAPI } from "../../services/api";
import { useAI } from "../../context/AIContext";
import toast from "react-hot-toast";
import {
  X, Send, Sparkles, CheckCircle2, Loader2,
  Pencil, ChevronRight, Plus, Trash2,
} from "lucide-react";

// ── Re-export so consumers can import from a single entry point ──────────────

export { AddChainWizard } from "./AddChainWizard";

// ── Palette ───────────────────────────────────────────────────────────────────
const P = {
  violet50:  "var(--app-primary-soft)",
  violet100: "#CECBF6",
  violet200: "#AFA9EC",
  violet600: "var(--lyra-color-primary)",
  violet800: "#3C3489",
  teal50:    "var(--app-primary-soft)",
  teal100:   "#9FE1CB",
  teal600:   "var(--lyra-color-success)",
  amber50:   "var(--lyra-color-warning-bg)",
  amber100:  "#FAC775",
  amber800:  "#412402",
  green50:   "var(--app-primary-soft)",
  green100:  "#C0DD97",
  green600:  "var(--lyra-color-success)",
  green800:  "#173404",
  surface:   "var(--lyra-color-surface)",
  surface2:  "var(--lyra-color-surface-2)",
  border:    "var(--lyra-color-border)",
  border2:   "var(--app-border-strong)",
  ink:       "var(--lyra-color-text)",
  ink2:      "var(--app-text-2)",
  ink3:      "var(--lyra-color-text-muted)",
  ink4:      "var(--app-text-3)",
};

// ── Pill component ─────────────────────────────────────────────────────────────
function Pill({ label, variant = "cap", onRemove, animate = false }) {
  const styles = {
    cap:   { bg: P.violet50,  border: P.violet100, color: P.violet800 },
    skill: { bg: P.teal50,    border: P.teal100,   color: P.teal600   },
    new:   { bg: P.amber50,   border: P.amber100,  color: P.amber800  },
  };
  const s = styles[variant] || styles.cap;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 99,
      background: s.bg, border: `0.5px solid ${s.border}`, color: s.color,
      animation: animate ? "pillPop .25s ease both" : "none",
    }}>
      {label}
      {onRemove && (
        <button onClick={onRemove} style={{
          background: "none", border: "none", cursor: "pointer",
          color: s.color, padding: 0, lineHeight: 1, fontSize: 12,
        }}>×</button>
      )}
    </span>
  );
}

// ── AI avatar ─────────────────────────────────────────────────────────────────
function AIAvatar() {
  return (
    <div style={{
      width: 22, height: 22, borderRadius: 7, background: P.violet50,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, marginBottom: 2,
    }}>
      <Sparkles size={11} color={P.violet600} />
    </div>
  );
}

// ── Chat bubble ───────────────────────────────────────────────────────────────
function Bubble({ type, children }) {
  const isAI = type === "ai";
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: isAI ? "flex-start" : "flex-end",
      gap: 2, animation: "fadeUp .28s ease both",
    }}>
      {isAI && <AIAvatar />}
      <div style={{
        padding: "8px 12px", borderRadius: 12, fontSize: 12.5,
        lineHeight: 1.65, maxWidth: "88%",
        borderBottomLeftRadius: isAI ? 3 : 12,
        borderBottomRightRadius: isAI ? 12 : 3,
        background: isAI ? P.surface2 : "var(--lyra-color-primary)",
        color: isAI ? P.ink : "var(--lyra-color-surface)",
        border: isAI ? `0.5px solid ${P.border}` : "none",
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Typing indicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
      <AIAvatar />
      <div style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "8px 12px", borderRadius: 12, borderBottomLeftRadius: 3,
        background: P.surface2, border: `0.5px solid ${P.border}`,
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 5, height: 5, borderRadius: "50%",
            background: P.violet200,
            animation: `typingBounce .9s ease infinite`,
            animationDelay: `${i * 0.15}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Suggestion chips ──────────────────────────────────────────────────────────
function SuggestionChips({ chips, onPick }) {
  if (!chips || chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: "4px 14px 8px" }}>
      {chips.map(c => (
        <button key={c} onClick={() => onPick(c)} style={{
          fontSize: 11, padding: "4px 11px", borderRadius: 99,
          border: `0.5px solid ${P.border2}`,
          background: P.surface, color: P.ink3,
          cursor: "pointer", transition: "all .15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = P.violet50; e.currentTarget.style.borderColor = P.violet100; e.currentTarget.style.color = P.violet800; }}
          onMouseLeave={e => { e.currentTarget.style.background = P.surface; e.currentTarget.style.borderColor = P.border2; e.currentTarget.style.color = P.ink3; }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

// ── Live agent card preview ───────────────────────────────────────────────────
function AgentCardPreview({ agent, isReady }) {
  return (
    <div style={{
      background: isReady ? P.violet50 : P.surface2,
      border: `0.5px solid ${isReady ? P.violet200 : P.border}`,
      borderRadius: 14, padding: 14,
      display: "flex", flexDirection: "column", gap: 10,
      transition: "all .4s ease",
    }}>
      {/* top row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 11,
          background: isReady ? P.violet100 : P.border,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0, transition: "background .4s",
        }}>
          {agent.icon || "🤖"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 700,
            color: agent.name ? P.ink : P.ink4,
          }}>
            {agent.name || "Untitled agent"}
          </div>
          <div style={{ fontSize: 11.5, color: P.ink3, marginTop: 2, lineHeight: 1.4 }}>
            {agent.description || "Describe your agent to get started..."}
          </div>
        </div>
      </div>

      {/* skills */}
      {agent.skills?.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: P.ink4, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>Skills</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {agent.skills.map(s => <Pill key={s} label={s} variant="skill" animate />)}
          </div>
        </div>
      )}

      {/* capabilities */}
      {agent.capabilities?.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: P.ink4, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>Capabilities</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {agent.capabilities.map(c => <Pill key={c} label={c} variant="cap" animate />)}
          </div>
        </div>
      )}

      {/* system prompt preview */}
      {agent.systemPrompt && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: P.ink4, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>System prompt</div>
          <div style={{
            fontFamily: "monospace", fontSize: 10.5,
            color: P.ink3, background: P.surface,
            border: `0.5px solid ${P.border}`, borderRadius: 8,
            padding: "7px 10px", lineHeight: 1.7,
            maxHeight: 56, overflow: "hidden", position: "relative",
          }}>
            {agent.systemPrompt}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              height: 16, background: "linear-gradient(transparent, var(--lyra-color-surface))",
            }} />
          </div>
        </div>
      )}

      {/* model */}
      {agent.defaultModel && (
        <div style={{ fontSize: 11, color: P.ink4 }}>
          Model: <span style={{ color: P.ink3, fontWeight: 500 }}>{agent.defaultModel}</span>
          {agent.schedule && <> · Schedule: <span style={{ color: P.ink3, fontWeight: 500 }}>{agent.schedule}</span></>}
        </div>
      )}
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export const AGENT_WIZARD_SYSTEM_PROMPT = `You are an agent-building assistant inside WorKult, an AI productivity workspace.
Your job is to help users create custom AI agents through friendly conversation.

When a user describes what they want, extract:
- name: short memorable agent name
- icon: single emoji that fits the domain
- description: 1 sentence, what the agent does
- personality: tone/style in 5 words or less
- capabilities: array of 2-4 snake_case tags (e.g. search_web, draft_email)
- skills: array chosen ONLY from: web-search, url-scraper, file-processor, data-parser, google-calendar, pm-projects, pm-tasks, gmail, telegram, whatsapp, kult-memory
- systemPrompt: a focused system prompt (3-6 sentences)
- defaultModel: "gemini" (always default unless user specifies)
- examples: array of 2-3 example user prompts for this agent

Ask at most ONE clarifying question if truly needed. Never ask multiple questions at once.
When you have enough info, respond with JSON in this exact shape:
{ "message": "conversational reply to user", "agentDraft": { name, icon, description, personality, capabilities, skills, systemPrompt, defaultModel, examples }, "suggestions": ["chip1","chip2","chip3"], "isReady": false }

When the agent is fully defined set isReady: true.
Always include "message" and "suggestions" (3 short chips). Keep agentDraft null until you have enough to fill it meaningfully.
Respond ONLY with valid JSON — no markdown fences.`;

export function AddAgentWizard({ onClose, onCreated, existingAgents = [], editAgent = null }) {
  const isEdit = Boolean(editAgent);
  const { models, selectedModelId, customApiKeys } = useAI();
  // wizardModel: which model powers the wizard conversation AND becomes defaultModel
  const [wizardModel, setWizardModel] = useState(selectedModelId || "");

  const [messages, setMessages] = useState([]);   // { role: "user"|"ai", text, pills }
  const [agentDraft, setAgentDraft] = useState(isEdit ? {
    name: editAgent.name || "",
    icon: editAgent.icon || "🤖",
    description: editAgent.description || "",
    personality: editAgent.personality || "",
    systemPrompt: editAgent.systemPrompt || "",
    capabilities: editAgent.capabilities || [],
    skills: editAgent.skills || [],
    examples: editAgent.examples || [],
    defaultModel: editAgent.defaultModel || wizardModel || selectedModelId || "gemini",
    maxTokens: editAgent.maxTokens || 2048,
    schedule: editAgent.schedule || "",
    goal: editAgent.goal || "",
    enabled: editAgent.enabled !== false,
  } : null);
  const [isReady, setIsReady] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // opening AI greeting
  useEffect(() => {
    const greeting = isEdit
      ? `Let's update ${editAgent.name}. What would you like to change?`
      : "What should your new agent do? Describe it in plain English — I'll handle the rest.";
    setMessages([{ role: "ai", text: greeting }]);
    setSuggestions(["Book movie tickets", "Search & reply to emails", "Morning news digest"]);
  }, []);

  // ── Call AI via server (respects user's selected model + auto fallback) ─────
  const callAI = useCallback(async (userMessage) => {
  const newHistory = [...conversationHistory, { role: "user", content: userMessage }];
  setConversationHistory(newHistory);
  setIsTyping(true);
  try {
    const data = await customAgentAPI.wizardChat(
      newHistory,
      AGENT_WIZARD_SYSTEM_PROMPT,
      wizardModel || undefined,
      wizardModel ? (customApiKeys?.[wizardModel] || undefined) : undefined
    );
    const raw = typeof data.result === "string" ? data.result : JSON.stringify(data.result);
    let parsed;
    try { parsed = JSON.parse(raw.replace(/```json|```/g, "").trim()); }
    catch { parsed = { message: "Can you tell me more about what this agent should do?", suggestions: [] }; }

    setConversationHistory(h => [...h, { role: "assistant", content: raw }]);
    setMessages(m => [...m, { role: "ai", text: parsed.message || "..." }]);
    setSuggestions(parsed.suggestions || []);
    if (parsed.agentDraft) {
      setAgentDraft(prev => ({
        ...prev,
        ...parsed.agentDraft,
        maxTokens: prev?.maxTokens || 2048,
        schedule: prev?.schedule || "",
        goal: prev?.goal || "",
        enabled: prev?.enabled !== false,
      }));
    }
    if (parsed.isReady) setIsReady(true);
  } catch {
    setMessages(m => [...m, { role: "ai", text: "Something went wrong. Try again." }]);
  } finally {
    setIsTyping(false);
  }
}, [conversationHistory, wizardModel, customApiKeys]);

  // ── Send a message ─────────────────────────────────────────────────────────
  const handleSend = useCallback((text) => {
    const val = (text || inputVal).trim();
    if (!val || isTyping) return;
    setInputVal("");
    setSuggestions([]);
    setMessages(m => [...m, { role: "user", text: val }]);
    callAI(val);
  }, [inputVal, isTyping, callAI]);

  // ── Save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!agentDraft?.systemPrompt?.trim()) {
      toast.error("Keep chatting — the agent needs a system prompt before saving.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...agentDraft,
        examples: agentDraft.examples?.filter(e => e.trim()) || [],
        defaultModel: agentDraft.defaultModel || wizardModel || selectedModelId || "gemini",
      };
      if (isEdit) {
        const { agent } = await customAgentAPI.update(editAgent._id, payload);
        toast.success(`✏️ "${agent.name}" updated!`);
        onCreated(agent);
      } else {
        const { agent } = await customAgentAPI.create(payload);
        toast.success(`✅ "${agent.name}" created!`);
        onCreated(agent);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to save agent");
    } finally {
      setSaving(false);
    }
  }

  // ── Advanced panel edits ───────────────────────────────────────────────────
  function patchDraft(key, val) {
    setAgentDraft(d => ({ ...d, [key]: val }));
  }

  const canSave = agentDraft?.name && agentDraft?.systemPrompt;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes typingBounce { 0%,60%,100% { transform:translateY(0); } 30% { transform:translateY(-4px); } }
        @keyframes pillPop { from { opacity:0; transform:scale(.85); } to { opacity:1; transform:scale(1); } }
        @keyframes spin { to { transform:rotate(360deg); } }
        .wiz-chip:hover { background:${P.violet50}!important; border-color:${P.violet100}!important; color:${P.violet800}!important; }
        .wiz-send:hover { background:#1d4ed8!important; }
        .wiz-launch:hover { background:var(--lyra-color-success)!important; }
        .wiz-inp:focus { outline:none; border-color:var(--lyra-color-primary)!important; }
      `}</style>

      {/* Overlay */}
      <div
        onClick={e => e.target === e.currentTarget && onClose()}
        style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(15,10,40,0.6)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}
      >
        {/* Modal */}
        <div style={{
          background: P.surface, borderRadius: 20, width: "100%", maxWidth: 820,
          maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column",
          border: `0.5px solid ${P.border}`,
        }}>

          {/* Header */}
          <div style={{
            padding: "14px 20px", background: P.violet50,
            borderBottom: `0.5px solid ${P.violet100}`,
            display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9, background: P.violet100,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {isEdit ? <Pencil size={15} color={P.violet600} /> : <Sparkles size={15} color={P.violet600} />}
            </div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: P.violet800 }}>
                {isEdit ? `Edit agent — ${editAgent.name}` : "Build an agent"}
              </div>
              <div style={{ fontSize: 11, color: P.violet600, marginTop: 1 }}>
                {isEdit ? "Update any detail by chatting" : "Just describe what you need — AI builds the rest"}
              </div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              {isReady && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "3px 10px", background: P.green50,
                  border: `0.5px solid ${P.green100}`, borderRadius: 99,
                  fontSize: 11, fontWeight: 500, color: P.green600,
                }}>
                  <CheckCircle2 size={11} color={P.green600} />
                  Ready
                </div>
              )}
              <button onClick={onClose} style={{
                width: 28, height: 28, borderRadius: 7,
                background: "rgba(83,74,183,0.1)", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}>
                <X size={13} color={P.violet600} />
              </button>
            </div>
          </div>

          {/* Body: split */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            flex: 1, overflow: "hidden", minHeight: 0,
          }}>

            {/* LEFT: chat */}
            <div style={{
              display: "flex", flexDirection: "column",
              borderRight: `0.5px solid ${P.border}`, overflow: "hidden",
            }}>
              {/* messages */}
              <div style={{
                flex: 1, overflowY: "auto", padding: "16px 14px 8px",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                {messages.map((m, i) => (
                  <Bubble key={i} type={m.role}>{m.text}</Bubble>
                ))}
                {isTyping && <TypingIndicator />}
                <div ref={chatEndRef} />
              </div>

              {/* suggestion chips */}
              <SuggestionChips chips={suggestions} onPick={t => handleSend(t)} />

              {/* input */}
              <div style={{
                borderTop: `0.5px solid ${P.border}`,
                padding: "10px 14px 12px",
                display: "flex", gap: 8, alignItems: "flex-end",
              }}>
                <textarea
                  ref={inputRef}
                  className="wiz-inp"
                  value={inputVal}
                  onChange={e => setInputVal(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Describe your agent..."
                  rows={2}
                  style={{
                    flex: 1, resize: "none",
                    border: `0.5px solid ${P.border2}`, borderRadius: 10,
                    background: P.surface2, padding: "8px 11px",
                    fontSize: 12.5, color: P.ink, lineHeight: 1.5,
                    fontFamily: "inherit",
                  }}
                />
                <button
                  className="wiz-send"
                  onClick={() => handleSend()}
                  disabled={!inputVal.trim() || isTyping}
                  style={{
                    width: 36, height: 36, borderRadius: 9, border: "none",
                    background: inputVal.trim() && !isTyping ? "var(--lyra-color-primary)" : P.border,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: inputVal.trim() && !isTyping ? "pointer" : "not-allowed",
                    flexShrink: 0, transition: "background .15s",
                  }}
                >
                  {isTyping
                    ? <Loader2 size={14} color="var(--app-on-accent)" style={{ animation: "spin 1s linear infinite" }} />
                    : <Send size={14} color="var(--app-on-accent)" />
                  }
                </button>
              </div>
            </div>

            {/* RIGHT: live preview */}
            <div style={{
              display: "flex", flexDirection: "column",
              overflow: "hidden",
            }}>
              <div style={{ padding: "8px 12px", borderBottom: `0.5px solid ${P.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: P.surface2, flexShrink: 0, gap: 8,
              }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: P.ink3, flexShrink: 0 }}>Agent preview</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, justifyContent: "flex-end" }}>
                  <select
                    value={wizardModel}
                    onChange={e => { setWizardModel(e.target.value); patchDraft("defaultModel", e.target.value); }}
                    style={{
                      fontSize: 10.5, padding: "3px 6px", borderRadius: 6, fontFamily: "inherit",
                      border: `0.5px solid ${P.border2}`, background: P.surface, color: P.ink3,
                      maxWidth: 130, cursor: "pointer",
                    }}
                  >
                    <option value="">Auto (best available)</option>
                    {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <div style={{ fontSize: 10.5, color: isReady ? P.green600 : P.ink4, flexShrink: 0 }}>
                    {isReady ? "● ready" : "● assembling..."}
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                <AgentCardPreview agent={agentDraft || {}} isReady={isReady} />

                {/* advanced toggle */}
                {agentDraft && (
                  <div>
                    <button
                      onClick={() => setShowAdvanced(v => !v)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 11, color: P.ink4, padding: 0,
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      <ChevronRight size={11} style={{ transform: showAdvanced ? "rotate(90deg)" : "none", transition: "transform .2s" }} />
                      Advanced settings
                    </button>

                    {showAdvanced && (
                      <div style={{
                        marginTop: 10, display: "flex", flexDirection: "column", gap: 10,
                        background: P.surface2, borderRadius: 10, padding: 12,
                        border: `0.5px solid ${P.border}`, animation: "fadeUp .2s ease",
                      }}>
                        {/* model */}
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: P.ink4, marginBottom: 5 }}>Model</div>
                          <select
                            value={agentDraft.defaultModel}
                            onChange={e => patchDraft("defaultModel", e.target.value)}
                            style={{
                              width: "100%", padding: "6px 10px", borderRadius: 8,
                              border: `0.5px solid ${P.border2}`, background: P.surface,
                              fontSize: 12, color: P.ink,
                            }}
                          >
                            <option value="">Auto (best available)</option>
                            {models.map(m => <option key={m.id} value={m.id}>{m.name}{m.badge ? ` · ${m.badge}` : ""}</option>)}
                          </select>
                        </div>

                        {/* max tokens */}
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: P.ink4, marginBottom: 5 }}>Max tokens</div>
                          <input
                            type="number"
                            value={agentDraft.maxTokens}
                            onChange={e => patchDraft("maxTokens", Number(e.target.value))}
                            style={{
                              width: "100%", padding: "6px 10px", borderRadius: 8,
                              border: `0.5px solid ${P.border2}`, background: P.surface,
                              fontSize: 12, color: P.ink,
                            }}
                          />
                        </div>

                        {/* schedule */}
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: P.ink4, marginBottom: 5 }}>Cron schedule <span style={{ fontWeight: 400 }}>(leave blank for on-demand)</span></div>
                          <input
                            value={agentDraft.schedule}
                            onChange={e => patchDraft("schedule", e.target.value)}
                            placeholder="0 9 * * 1-5"
                            style={{
                              width: "100%", padding: "6px 10px", borderRadius: 8,
                              border: `0.5px solid ${P.border2}`, background: P.surface,
                              fontSize: 12, color: P.ink,
                            }}
                          />
                        </div>

                        {/* examples */}
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: P.ink4, marginBottom: 5 }}>Example prompts</div>
                          {(agentDraft.examples || []).map((ex, i) => (
                            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                              <input
                                value={ex}
                                onChange={e => {
                                  const arr = [...(agentDraft.examples || [])];
                                  arr[i] = e.target.value;
                                  patchDraft("examples", arr);
                                }}
                                placeholder={`Example ${i + 1}...`}
                                style={{
                                  flex: 1, padding: "5px 9px", borderRadius: 7,
                                  border: `0.5px solid ${P.border2}`, background: P.surface,
                                  fontSize: 11.5, color: P.ink,
                                }}
                              />
                              <button onClick={() => patchDraft("examples", agentDraft.examples.filter((_, j) => j !== i))}
                                style={{ padding: "5px 7px", borderRadius: 7, border: `0.5px solid var(--app-danger-border)`, background: "var(--lyra-color-danger-bg)", cursor: "pointer", display: "flex" }}>
                                <Trash2 size={12} color="var(--lyra-color-danger)" />
                              </button>
                            </div>
                          ))}
                          {(agentDraft.examples?.length || 0) < 4 && (
                            <button
                              onClick={() => patchDraft("examples", [...(agentDraft.examples || []), ""])}
                              style={{
                                display: "flex", alignItems: "center", gap: 5, fontSize: 11,
                                color: P.violet600, background: "none", border: "none", cursor: "pointer", padding: "2px 0",
                              }}
                            >
                              <Plus size={11} /> Add example
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* launch buttons */}
              {isReady && (
                <div style={{
                  padding: "12px 14px", borderTop: `0.5px solid ${P.border}`,
                  display: "flex", gap: 8, flexShrink: 0,
                }}>
                  <button
                    className="wiz-launch"
                    onClick={handleSave}
                    disabled={saving || !canSave}
                    style={{
                      flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                      background: canSave ? P.green600 : P.border,
                      color: "var(--app-on-accent)", fontSize: 13, fontWeight: 600,
                      cursor: canSave ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      transition: "background .2s",
                    }}
                  >
                    {saving
                      ? <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> Saving...</>
                      : <><CheckCircle2 size={14} /> {isEdit ? "Save changes" : "Launch agent"}</>
                    }
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
